import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Typography
} from '@material-ui/core'
import BlockIcon from '@material-ui/icons/Block'
import Alert from '@material-ui/lab/Alert'
import React, { useState } from 'react'
import { toast } from 'react-toastify'

import { ConfirmActionModal } from '../../components/ConfirmActionModal'
import SectionTitle from '../../components/SectionTitle'
import VisuallyHidden from '../../components/VisuallyHidden'
import {
  RETIRE_TICKET_SLA_POLICY_MUTATION,
  TICKET_SLA_POLICIES_QUERY
} from '../../graphql/tickets'
import { SlaPolicy, sortPolicies } from '../../helpers/slaPolicies'
import { getTranslate } from '../../localization'
import SlaPoliciesTable from './SlaPoliciesTable'
import SlaPolicyForm from './SlaPolicyForm'

/**
 * SLA policy administration (BL-034).
 *
 * Editing is versioned: saving retires the current version and inserts the
 * next, so a ticket opened last week still resolves to the numbers it was
 * promised. Nothing here mutates a policy in place.
 */
export default function SupportSlaPolicies() {
  const [includeRetired, setIncludeRetired] = useState(false)
  // Retirement silently changes which SLA governs a customer's future tickets,
  // and it is not visible anywhere until a ticket is filed under the wrong
  // target. The app's ConfirmActionModal requires typing a signature phrase
  // rather than clicking OK — the right weight for an action you should not be
  // able to trigger by muscle memory.
  const [pendingRetire, setPendingRetire] = useState<SlaPolicy | null>(null)
  // Three states, not two: closed, creating, editing a specific version.
  // A boolean plus a separate "editing" value can express "open but editing
  // nothing", which is a create the user did not ask for.
  const [form, setForm] = useState<
    { mode: 'create' } | { mode: 'edit'; policy: SlaPolicy } | null
  >(null)

  const { data, loading, error, refetch } = useQuery<{
    ticketSlaPolicies: SlaPolicy[]
  }>(TICKET_SLA_POLICIES_QUERY, {
    variables: { includeRetired },
    notifyOnNetworkStatusChange: true
  })

  // Returns the retired version, so the cache entry for that policy updates in
  // place. The list itself is refetched because retirement removes a row from
  // the active view entirely, which normalisation alone cannot express.
  const [retire, retireState] = useMutation<{
    retireTicketSlaPolicy: SlaPolicy
  }>(RETIRE_TICKET_SLA_POLICY_MUTATION, {
    refetchQueries: [
      { query: TICKET_SLA_POLICIES_QUERY, variables: { includeRetired } }
    ]
  })

  const policies = sortPolicies(data?.ticketSlaPolicies ?? [])

  function onRetire(policy: SlaPolicy) {
    setPendingRetire(null)
    retire({
      variables: {
        policyId: policy.policyId,
        // The version being retired. A stale value is rejected by the server
        // rather than silently retiring a version this screen never showed.
        expectedVersion: policy.version
      }
    }).catch((e) => toast.error(e.message))
  }

  return (
    <div>
      {/* No PageHeader: this is a TAB now and SupportTabs sets the document
          title. Two mounted together fight over it, and the parent wins. */}
      <SectionTitle title={getTranslate('Support SLA policies')} />

      <Typography variant="body2" color="textSecondary">
        {getTranslate(
          'Targets are elapsed time on a 24/7 clock. A customer override outranks a platform policy, even a narrower one.'
        )}
      </Typography>

      <Button
        variant="contained"
        color="primary"
        style={{ marginTop: 8 }}
        onClick={() => setForm({ mode: 'create' })}>
        {getTranslate('New policy')}
      </Button>

      {error && <Alert severity="error">{error.message}</Alert>}

      <FormControlLabel
        control={
          <Switch
            checked={includeRetired}
            onChange={(_e, checked) => setIncludeRetired(checked)}
            color="primary"
          />
        }
        label={getTranslate('Show retired versions')}
      />

      <Paper style={{ marginTop: 8 }}>
        <SlaPoliciesTable
          reloadData={() => refetch?.()}
          policies={policies}
          loading={loading}
          settled={!loading && !error}
          retiring={retireState.loading}
          onEdit={(policy) => setForm({ mode: 'edit', policy })}
          onRetire={setPendingRetire}
        />

        {/* Only while data is ALREADY on screen — that is the "toggle show
            retired" case this announces, where nothing else visibly changes.
            The first load is covered by the skeleton rows instead; showing a
            spinner over them would report the same state twice. */}
        {loading && policies.length > 0 && (
          <div
            role="status"
            aria-live="polite"
            style={{ padding: 16, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <VisuallyHidden>{getTranslate('Loading')}</VisuallyHidden>
          </div>
        )}
      </Paper>

      {pendingRetire && (
        <ConfirmActionModal
          icon={<BlockIcon />}
          // <bdi> here for the same reason the table cell uses it: the title
          // is a Latin identifier dropped into an otherwise RTL dialog, and
          // the bidi algorithm reorders "POLICY-1 v2" without it.
          title={
            <bdi dir="ltr">{`${pendingRetire.policyId} v${pendingRetire.version}`}</bdi>
          }
          // Names the consequence rather than asking "are you sure": tickets
          // filed after this fall through to the next-most-specific policy,
          // and nothing on screen will show that they did.
          subtitle={getTranslate(
            'Tickets opened after this will fall through to the next matching policy. Existing tickets keep the targets they were given.'
          )}
          // Translated, because the operator has to TYPE it. ConfirmActionModal
          // interpolates the signature raw, so an untranslated phrase asks a
          // Persian admin to switch keyboard layout to retire a policy — and
          // the gate exists to make the action deliberate, not to obstruct it.
          //
          // Fixed at the call site rather than in the component: nine other
          // callers pass English phrases, and one passes a promotion NAME,
          // which must never be translated. That is a separate change.
          signature={getTranslate('Retire')}
          isOpen={Boolean(pendingRetire)}
          onClose={() => setPendingRetire(null)}
          onClick={() => onRetire(pendingRetire)}
          loading={retireState.loading}
        />
      )}

      {form && (
        <SlaPolicyForm
          // Keyed so switching which policy is being edited remounts the form.
          // The draft is seeded once from `editing`, so a reused instance would
          // keep the previous policy's values while claiming to edit this one.
          key={
            form.mode === 'edit'
              ? `${form.policy.policyId}-${form.policy.version}`
              : 'create'
          }
          editing={form.mode === 'edit' ? form.policy : undefined}
          includeRetired={includeRetired}
          onClose={() => setForm(null)}
        />
      )}
    </div>
  )
}
